<template>
  <div class="card">
    <div class="card-body p-md-4">
      <form @submit.prevent="submit">
        <div class="form-row py-3">
          <div class="col-md-5">
            <input type="text" name="ordernr" class="form-control" placeholder="Ordernummer" v-model="form.ordernr" required>
          </div>
          <div class="col-md-3">
            <input type="text" name="postcode" class="form-control" placeholder="Postcode" v-model="form.postcode" required>
          </div>
          <div class="col-md-4">
            <button class="btn btn-primary btn-block">Track & Trace
              <span v-if="loading"><i class="fas fa-spinner fa-pulse"></i></span>
            </button>
          </div>
        </div>
      </form>
      <div v-if="loading" class="text-center loading">
        <i class="fas fa-spinner fa-pulse"></i>
      </div>
      <div v-if="error" class="alert alert-info" v-text="error"></div>
      <div v-if="response">
        <hr>
        <div v-if="reschedule" class="alert alert-success mb-3">Gewenste datum is verplaatst. Zodra de routes bekend zijn, zal hier uw tijdsvenster zichtbaar zijn.</div>
        <p class="text-uppercase font-weight-bolder mb-0">Jouw bestelling: {{ response.reference }}</p>
        <p class="text-uppercase font-weight-bolder"><small>Opdrachtgever: {{ response.client }}</small></p>

        <div class="row my-2">
          <div class="col-12 col-md-3 mb-3 text-center" :class="response.status.aangemeld ? 'done' : ''">
            <span class="circle mx-auto rounded-circle">
              <i class="fal fa-box fa-lg"></i>
            </span>
            <p class="mt-2 mb-0 font-weight-bold">Aangemeld</p>
          </div>
          <div class="col-12 col-md-3 mb-3 text-center" :class="response.status.warehouse ? 'done' : ''">
            <span class="circle mx-auto rounded-circle">
              <i class="fal fa-warehouse fa-lg"></i>
            </span>
            <p class="mt-2 mb-0 font-weight-bold">Sorteercentrum</p>
          </div>
          <div class="col-12 col-md-3 mb-3 text-center" :class="response.status.onderweg ? 'done' : ''">
            <span class="circle mx-auto rounded-circle">
              <i class="fal fa-shipping-fast fa-lg"></i>
            </span>
            <p class="mt-2 mb-0 font-weight-bold">Onderweg</p>
          </div>
          <div class="col-12 col-md-3 mb-3 text-center" :class="response.status.afgerond ? 'done' : ''">
            <span class="circle mx-auto rounded-circle">
              <i class="fas fa-box-check fa-lg"></i>
            </span>
            <p class="mt-2 mb-0 font-weight-bold">Afgerond</p>
          </div>
        </div>
        <hr>
        <div v-if="! response.status.afgerond">
          <p class="text-uppercase mb-2"><small>Verwacht {{ ['Afhaal', 'Afhaal installatie'].includes(response.type) ? 'afhaalmoment' : 'bezorgmoment' }}:</small></p>
          <p class="font-weight-bolder h2 mb-3">{{ leverdatum }}</p>
          <div v-if="response.scheduled_ts === '-'" class="alert alert-info"><i class="fas fa-info-circle"></i> Zodra de routes bekend zijn, zal hier uw tijdsvenster zichtbaar zijn.</div>
          <div v-else>Tussen {{ response.scheduled_ts }}</div>

          <div v-if="! canReschedule && response.scheduled_date" class="alert alert-succes">
            Wilt u uw afspraak verzetten? Neem dan contact met ons op en bel <a href="tel:+31307620610">030 762 06 10</a>.
          </div>

          <div v-if="! response.status.afgerond && canReschedule ">
            <p v-if="! reschedule" class="text-uppercase mb-2"><small>Liever op een andere dag?</small></p>
            <button v-if="! reschedule" class="btn btn-primary mb-3" @click="reschedule = true">Datum wijzigen &raquo;</button>
            <div v-if="reschedule">
              <p class="text-uppercase mb-2"><small>Selecteer een nieuwe datum:</small></p>
              <datepicker
                  v-model="state.date"
                  :language="datepicker.nl"
                  :disabled-dates="disabledDates"
                  :highlighted="highlighted"
                  name="reschedule"
                  :inline="true"
              ></datepicker>
              <button
                  @click="doReschedule"
                  class="btn btn-primary my-3"
                  :disabled="!state.date">Verzetten &raquo;</button>
            </div>
          </div>
          <hr>
          <div class="jumbotron p-3">
            <strong>{{ response.customer.name }}</strong><br>
            {{ response.customer.address }}<br>
            {{ response.customer.postcode }} {{ response.customer.city }}
          </div>
        </div>
      </div>
      <p class="text-black-50"><strong>Klantenservice</strong><br> Vragen over uw bestelling? Kijk bij onze <a
          href="/veelgestelde-vragen/">veelgestelde vragen</a>.</p>

    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import {en, nl} from 'vuejs-datepicker/dist/locale'
import moment from "moment";

export default {
  name: 'TrackAndTrace',

  components: { Datepicker },

  props: {
    ordernr: { type: String },
    postcode: { type: String },
    redirect: { type: Boolean, default: false }
  },

  data: () => ({
    loading: false,
    response: null,
    error: null,
    form: {
      ordernr: '',
      postcode: '',
    },
    reschedule: false,
    state: {
      date: null
    },
    datepicker: {
      en: en,
      nl: nl,
    },
    verzet: false
  }),

  computed: {
    leverdatum () {
      moment.locale('nl');
      return this.response.scheduled_date ? moment(this.response.scheduled_date).format('dddd DD MMMM') : 'NOG NIET BEKEND';
    },

    canReschedule () {
      return ! ["Installatie", "ServiceOnly", "Afhaal installatie"].includes(this.response.type) && this.response.can_reschedule;
    },

    disabledDates () {
      let cutoff = moment('14:00', 'hh:mm');
      let date = moment().isBefore(cutoff)
        ? moment()
        : moment().add(1, 'day');

      let original = moment(this.response.scheduled_date);
      if (original.isAfter(date)) {
        date = original;
      }

      let dates = [];
      if ('disabled_dates' in this.response) {
        this.response.disabled_dates.forEach(date => dates.push(new Date(date)));
      }

      return {
        dates,
        to: date.toDate(),
        days: [0]
      }
    },

    highlighted () {
      if (! this.response) return {};

      return {
        dates: [ moment(this.response.scheduled_date).toDate() ],
        includeDisabled: true
      };
    },

    apiUrl () {
      switch (process.env.NODE_ENV) {
        case 'development': return 'https://mijn.firstlogistics.test/api'; break;
        case 'staging': return 'https://mijn.acc.firstlogistics.nl/api'; break;
        default: return 'https://mijn.firstlogistics.nl/api';
      }
    }
  },

  mounted () {
    if (this.ordernr && this.postcode) {
      this.form.ordernr = this.ordernr;
      this.form.postcode = this.postcode;

      this.submit();
    }
  },

  methods: {
    submit() {
      if (this.redirect) {
        window.location.href = `/track-trace?tc=${this.form.ordernr}&pc=${this.form.postcode}`;
        return;
      }

      this.error = null;
      this.loading = true;
      this.response = null;
      axios
        .post(this.apiUrl + '/tracktrace', this.form)
        .then(response => {
          this.response = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.error = 'Zending niet gevonden. Probeer het nogmaals, controleer de berichten die u heeft ontvangen per e-mail of neem contact op met onze klantenservice.'
        });
    },

    doReschedule() {
      window.scrollTo(0, 0);

      this.error = null;
      this.loading = true;
      this.reschedule = false;

      let planning_id = this.response.planning_id;
      this.response = null;
      axios
          .patch(this.apiUrl + '/tracktrace', {
            ordernr: this.form.ordernr,
            postcode: this.form.postcode,
            planning: planning_id,
            date: moment(this.state.date).format('YYYY-MM-DD')
          })
          .then(response => {
            this.verzet = true;
            this.response = response.data;
            this.loading = false;
            this.state.date = null;
          })
          .catch(error => {
            this.state.date = null;
            this.loading = false;
            this.error = 'Zending niet gevonden. Probeer het nogmaals, controleer de berichten die u heeft ontvangen per e-mail of neem contact op met onze klantenservice.'
          });
    }
  }
}
</script>

<style lang="css" scoped>
.loading {
  min-height: 300px;
}
.circle {
  background: #e9ecef;
  width: 60px;
  height: 60px;
  line-height: 60px;
  display: inline-block;
}
.done .circle {
  background: linear-gradient(to right, #cc0000 0%, #ff3737 100%);
  color: #fff;
}
</style>
